import { ReactNode, useState } from 'react';
import { Button, Checkbox, DatePicker, Form, Input, Select, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import format from '@/utils/format';
import './TaskerApplication.scss';

/** Latest website application, as returned in `application` by GET /tasker/detail/{taskerId}. */
export interface TaskerApplication {
    applicationUuid: string;
    createTime: string;
    businessName?: string;
    abn?: string;
    services?: string[];
    regions?: string[];
    otherRegion?: string;
    travelRadius?: string;
    experience?: string;
    experienceDetails?: string;
    tools?: string;
    transport?: string;
    availableDays?: string[];
    availability?: string;
    insurance?: string;
    insurer?: string;
    insuranceCover?: string;
    insuranceExpiry?: string;
    licenceNumber?: string;
    licenceState?: string;
    licenceExpiry?: string;
    rightToWork?: boolean;
    infoAccurate?: boolean;
    reviewConsent?: boolean;
    applicationCount?: number | string;
}

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// The choices offered on the "Apply to become a tasker" website (tasker-onboarding/src/applicationForm.ts).
const SERVICES = [
    'Furniture assembly',
    'Sauna assembly',
    'Handyman',
    'Lawn mowing',
    'Electrical',
    'Plumbing',
    'Pressure washing',
    'TV mounting',
    'Deliveries',
    'Rubbish removal',
];
const REGIONS = [
    'Sydney',
    'Melbourne',
    'Brisbane',
    'Gold Coast',
    'Sunshine Coast',
    'Adelaide',
    'Perth',
    'Hobart',
    'Newcastle',
    'Wollongong',
    'Other',
];
const RADIUS = ['Up to 15 km', 'Up to 30 km', 'Up to 50 km', '50 km or more'];
const EXPERIENCE = ['Less than 1 year', '1–2 years', '3–5 years', '6+ years'];
const TOOLS = ['Yes', 'Some', 'Not yet'];
const TRANSPORT = ['Car', 'Van / ute', 'Truck', 'Other', 'No vehicle'];
const AVAILABILITY = ['Ready now', 'Within 2 weeks', 'Within a month', 'Later'];
const INSURANCE = ['Yes', 'Not yet'];

/** Options plus any stored value that is no longer offered, so editing never silently drops it. */
const optionsOf = (choices: string[], current?: string | string[]) => {
    const extra = ([] as string[]).concat(current ?? []).filter(v => v && !choices.includes(v));
    return [...choices, ...extra].map(v => ({ value: v, label: v }));
};

const toDay = (value?: string) => (value ? dayjs(value) : null);
const fromDay = (value?: Dayjs | null) => (value ? value.format('YYYY-MM-DD') : null);

/** "2027-06-30" → "30/06/2027" without a timezone shift. */
const formatDate = (value?: string) => {
    if (!value) {
        return '-';
    }
    const [y, m, d] = value.split('-');
    return d && m && y ? `${d}/${m}/${y}` : value;
};

const formatAbn = (abn?: string) => (abn ? abn.replace(/^(\d{2})(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3 $4') : '-');

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="tasker-application-field">
        <div className="tasker-application-label">{label}</div>
        <div className="tasker-application-value">{children || '-'}</div>
    </div>
);

const Chips = ({ items }: { items?: string[] }) =>
    items?.length ? (
        <div className="tasker-application-chips">
            {items.map(item => (
                <span className="category-i" key={item}>
                    {item}
                </span>
            ))}
        </div>
    ) : (
        <>-</>
    );

const Declaration = ({ ok, children }: { ok?: boolean; children: ReactNode }) => (
    <div className={ok ? 'tasker-application-declaration is-ok' : 'tasker-application-declaration'}>
        {ok ? '✓' : '✗'} {children}
    </div>
);

/** Everything the tasker filled in on the "Apply to become a tasker" website, for the admin's review. */
const required = (label: string) => [{ required: true, message: `${label} is required` }];

/** The application as an editable form; saves through `onSave` and returns to the read-only view. */
const TaskerApplicationEditForm = ({
    application,
    onSave,
    onCancel,
}: {
    application: TaskerApplication;
    onSave: (values: Record<string, unknown>) => Promise<void>;
    onCancel: () => void;
}) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const regions: string[] = Form.useWatch('regions', form) ?? application.regions ?? [];
    const insurance: string = Form.useWatch('insurance', form) ?? application.insurance;

    const save = async () => {
        const v = await form.validateFields();
        const insured = v.insurance === 'Yes';
        setSaving(true);
        try {
            await onSave({
                businessName: v.businessName ?? null,
                abn: v.abn ?? null,
                services: v.services,
                regions: v.regions,
                otherRegion: v.regions.includes('Other') ? v.otherRegion ?? null : null,
                travelRadius: v.travelRadius,
                experience: v.experience,
                experienceDetails: v.experienceDetails ?? null,
                tools: v.tools,
                transport: v.transport,
                availableDays: DAY_ORDER.filter(d => v.availableDays.includes(d)),
                availability: v.availability,
                insurance: v.insurance,
                // Like the website: insurer details only apply when the tasker says they are insured.
                insurer: insured ? v.insurer ?? null : null,
                insuranceCover: insured ? v.insuranceCover ?? null : null,
                insuranceExpiry: insured ? fromDay(v.insuranceExpiry) : null,
                licenceNumber: v.licenceNumber ?? null,
                licenceState: v.licenceState ?? null,
                licenceExpiry: fromDay(v.licenceExpiry),
                rightToWork: !!v.rightToWork,
                infoAccurate: !!v.infoAccurate,
                reviewConsent: !!v.reviewConsent,
            });
            onCancel();
        } catch (error: any) {
            message.error(error?.message ?? error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            className="tasker-application-form"
            initialValues={{
                ...application,
                insuranceExpiry: toDay(application.insuranceExpiry),
                licenceExpiry: toDay(application.licenceExpiry),
            }}
        >
            <div className="tasker-application-section">
                <h4>Business</h4>
                <div className="tasker-application-grid">
                    <Form.Item name="businessName" label="Trading name">
                        <Input maxLength={200} />
                    </Form.Item>
                    <Form.Item name="abn" label="ABN">
                        <Input maxLength={20} />
                    </Form.Item>
                </div>
            </div>

            <div className="tasker-application-section">
                <h4>Skills &amp; availability</h4>
                <div className="tasker-application-grid">
                    <Form.Item name="services" label="Services" rules={required('Services')}>
                        <Select mode="multiple" options={optionsOf(SERVICES, application.services)} />
                    </Form.Item>
                    <Form.Item name="regions" label="Service areas" rules={required('Service areas')}>
                        <Select mode="multiple" options={optionsOf(REGIONS, application.regions)} />
                    </Form.Item>
                    {regions.includes('Other') && (
                        <Form.Item name="otherRegion" label="Other area" rules={required('Other area')}>
                            <Input maxLength={200} />
                        </Form.Item>
                    )}
                    <Form.Item name="travelRadius" label="Travel radius" rules={required('Travel radius')}>
                        <Select options={optionsOf(RADIUS, application.travelRadius)} />
                    </Form.Item>
                    <Form.Item name="experience" label="Experience" rules={required('Experience')}>
                        <Select options={optionsOf(EXPERIENCE, application.experience)} />
                    </Form.Item>
                    <Form.Item name="tools" label="Own tools" rules={required('Own tools')}>
                        <Select options={optionsOf(TOOLS, application.tools)} />
                    </Form.Item>
                    <Form.Item name="transport" label="Transport" rules={required('Transport')}>
                        <Select options={optionsOf(TRANSPORT, application.transport)} />
                    </Form.Item>
                    <Form.Item name="availability" label="Can start" rules={required('Can start')}>
                        <Select options={optionsOf(AVAILABILITY, application.availability)} />
                    </Form.Item>
                </div>
                <Form.Item name="availableDays" label="Available days" rules={required('Available days')}>
                    <Checkbox.Group options={DAY_ORDER} />
                </Form.Item>
                <Form.Item name="experienceDetails" label="Relevant experience">
                    <Input.TextArea maxLength={2000} autoSize={{ minRows: 2, maxRows: 6 }} />
                </Form.Item>
            </div>

            <div className="tasker-application-section">
                <h4>Checks &amp; cover</h4>
                <div className="tasker-application-grid">
                    <Form.Item name="insurance" label="Public liability insurance" rules={required('Insurance')}>
                        <Select options={optionsOf(INSURANCE, application.insurance)} />
                    </Form.Item>
                    {insurance === 'Yes' && (
                        <>
                            <Form.Item name="insurer" label="Insurer">
                                <Input maxLength={200} />
                            </Form.Item>
                            <Form.Item name="insuranceCover" label="Cover">
                                <Input maxLength={100} />
                            </Form.Item>
                            <Form.Item name="insuranceExpiry" label="Insurance expiry">
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item name="licenceNumber" label="Trade licence">
                        <Input maxLength={100} />
                    </Form.Item>
                    <Form.Item name="licenceState" label="Issued in">
                        <Input maxLength={50} />
                    </Form.Item>
                    <Form.Item name="licenceExpiry" label="Licence expiry">
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                </div>
            </div>

            <div className="tasker-application-section">
                <h4>Declarations</h4>
                <Form.Item name="rightToWork" valuePropName="checked" className="tasker-application-check">
                    <Checkbox>Has the right to work in Australia</Checkbox>
                </Form.Item>
                <Form.Item name="infoAccurate" valuePropName="checked" className="tasker-application-check">
                    <Checkbox>Confirmed the information is accurate</Checkbox>
                </Form.Item>
                <Form.Item name="reviewConsent" valuePropName="checked" className="tasker-application-check">
                    <Checkbox>Consented to the application review</Checkbox>
                </Form.Item>
            </div>

            <div className="tasker-application-actions">
                <Button onClick={onCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button type="primary" loading={saving} onClick={save}>
                    Save
                </Button>
            </div>
        </Form>
    );
};

const TaskerApplicationDetails = ({
    application,
    onSave,
}: {
    application: TaskerApplication;
    /** When given, an Edit button lets the admin change the information. */
    onSave?: (values: Record<string, unknown>) => Promise<void>;
}) => {
    const [editing, setEditing] = useState(false);
    const regions = (application.regions ?? []).map(r =>
        r === 'Other' && application.otherRegion ? `Other: ${application.otherRegion}` : r
    );
    const days = [...(application.availableDays ?? [])].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
    const hasLicence = Boolean(application.licenceNumber);
    const count = Number(application.applicationCount ?? 1);

    return (
        <div className="tasker-application">
            <div className="tasker-application-header">
                <h3>Information</h3>
                <span className="tasker-application-meta">
                    Submitted {format.formatTimeMDY(application.createTime)}
                    {count > 1 ? ` · applied ${count} times` : ''} · Ref {application.applicationUuid.slice(0, 8)}
                    {onSave && !editing && (
                        <Button size="small" className="tasker-application-edit" onClick={() => setEditing(true)}>
                            Edit
                        </Button>
                    )}
                </span>
            </div>

            {editing && onSave ? (
                <TaskerApplicationEditForm
                    application={application}
                    onSave={onSave}
                    onCancel={() => setEditing(false)}
                />
            ) : (
                <>
                    <div className="tasker-application-section">
                        <h4>Business</h4>
                        <div className="tasker-application-grid">
                            <Field label="Trading name">{application.businessName}</Field>
                            <Field label="ABN">{formatAbn(application.abn)}</Field>
                        </div>
                    </div>

                    <div className="tasker-application-section">
                        <h4>Skills &amp; availability</h4>
                        <div className="tasker-application-grid">
                            <Field label="Services">
                                <Chips items={application.services} />
                            </Field>
                            <Field label="Service areas">
                                <Chips items={regions} />
                            </Field>
                            <Field label="Travel radius">{application.travelRadius}</Field>
                            <Field label="Experience">{application.experience}</Field>
                            <Field label="Own tools">{application.tools}</Field>
                            <Field label="Transport">{application.transport}</Field>
                            <Field label="Available days">
                                <Chips items={days} />
                            </Field>
                            <Field label="Can start">{application.availability}</Field>
                        </div>
                        <Field label="Relevant experience">
                            <div className="tasker-application-text">{application.experienceDetails}</div>
                        </Field>
                    </div>

                    <div className="tasker-application-section">
                        <h4>Checks &amp; cover</h4>
                        <div className="tasker-application-grid">
                            <Field label="Public liability insurance">{application.insurance}</Field>
                            {application.insurance === 'Yes' && (
                                <>
                                    <Field label="Insurer">{application.insurer}</Field>
                                    <Field label="Cover">{application.insuranceCover}</Field>
                                    <Field label="Insurance expiry">{formatDate(application.insuranceExpiry)}</Field>
                                </>
                            )}
                            <Field label="Trade licence">
                                {hasLicence ? application.licenceNumber : 'Not required'}
                            </Field>
                            {hasLicence && (
                                <>
                                    <Field label="Issued in">{application.licenceState}</Field>
                                    <Field label="Licence expiry">{formatDate(application.licenceExpiry)}</Field>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="tasker-application-section">
                        <h4>Declarations</h4>
                        <Declaration ok={application.rightToWork}>Has the right to work in Australia</Declaration>
                        <Declaration ok={application.infoAccurate}>Confirmed the information is accurate</Declaration>
                        <Declaration ok={application.reviewConsent}>Consented to the application review</Declaration>
                    </div>
                </>
            )}
        </div>
    );
};

export default TaskerApplicationDetails;
